import { DateRangePicker } from "components/UI/DateRangePicker/DateRangePicker";
import { useTable } from "components/UI/Table/hooks/useTable";
import { useTableUrlParameters } from "components/UI/Table/hooks/useTableUrlParameters";
import dayjs, { Dayjs } from "dayjs";
import { REPORT_DATE_FORMAT_WITH_TIME } from "localConstants";
import { useEffect, useMemo } from "react";
import { useRecoilState } from "recoil";
import { TableId } from "types/models";

import {
    timeFilterAtom,
    getPersistedTimeFilterValue,
    removePersistedTimeFilterValue,
    getPersistedRawTimeFilterValue,
    removePersistedRawTimeFilterValue,
    setPersistedRawTimeFilterValue,
} from "../../atoms/timeFilterAtom";
import { DateFilterMode } from "../../types/table";
import { getDefaultCreatedAtFilter } from "../../utils/getDefaultCreatedAtFilter";

interface TimeFilterProps {
    tableId: TableId;
    dataIndex: string;
    dateFilterMode?: DateFilterMode;
}

// Anything finer than the selected granularity is clamped automatically — 0 (start of unit) for
// the range start, max value (end of unit) for the range end — same as "date" always clamped
// time-of-day to 00:00:00 / 23:59:59.
const GRANULARITY_UNIT: Record<DateFilterMode, dayjs.OpUnitType> = {
    date: "day",
    hour: "hour",
    minute: "minute",
    second: "second",
};

export const TimeFilter = ({ tableId, dataIndex, dateFilterMode = "date" }: TimeFilterProps) => {
    const tableUtils = useTable(tableId, dataIndex);
    const tableUrlParametersUtils = useTableUrlParameters(tableId);
    const [timeFilter] = useRecoilState(timeFilterAtom);

    // "this month" fallback
    const defaultRange: [Dayjs, Dayjs] = [dayjs().subtract(30, "day").startOf("day"), dayjs().endOf("day")];

    // Helper to parse and validate date range string
    const parseDateRange = (value: string): [Dayjs, Dayjs] | null => {
        const parts = value.split(",");
        if (parts.length !== 2) return null;

        const start = dayjs(parts[0].trim(), REPORT_DATE_FORMAT_WITH_TIME);
        const end = dayjs(parts[1].trim(), REPORT_DATE_FORMAT_WITH_TIME);

        if (!start.isValid() || !end.isValid()) return null;
        return [start, end];
    };

    // Clamp a range to this table's granularity — anything finer is forced to 0/max for that unit.
    const clampToMode = (range: [Dayjs, Dayjs]): [Dayjs, Dayjs] => {
        const unit = GRANULARITY_UNIT[dateFilterMode];
        return [range[0].startOf(unit), range[1].endOf(unit)];
    };

    const rangesMatch = (left: [Dayjs, Dayjs], right: [Dayjs, Dayjs]) =>
        left[0].isSame(right[0]) && left[1].isSame(right[1]);

    // Apply the filter for this table (dataIndex + a value clamped to this table's granularity)
    // whenever we switch tables or the table's dataIndex/mode changes.
    //
    // Priority order for where the range comes from:
    //   1. The URL's OWN current value for this dataIndex — e.g. the user hand-edited the range
    //      (or page number) in the address bar and hit Enter. The URL is the explicit, just-set
    //      source of truth and must never be silently overwritten by a stale persisted value —
    //      that was exactly this bug: editing the date in the URL got reverted back on Enter
    //      because this effect always preferred sessionStorage over what was actually in the URL.
    //   2. The RAW persisted value (the highest-precision range the user actually picked, kept in
    //      a separate storage key — see timeFilterAtom.ts) — used only when the URL has nothing
    //      for this dataIndex (e.g. navigating to this table fresh with no filter in the URL).
    //      That's what lets a "minute"-precision pick made on one log page survive a visit to a
    //      "date"-only page: the "date"-only page clamps a copy for its own use but never
    //      overwrites the raw value, so switching back to a "minute" page restores the original
    //      precision instead of the coarse 00:00:00/23:59:59 the in-between page applied.
    useEffect(() => {
        const urlFilterForIndex = tableUrlParametersUtils.getUrlParameters()?.filter?.filter?.[dataIndex];
        const urlValue = urlFilterForIndex ? Object.values(urlFilterForIndex)[0] : undefined;
        const urlRange = typeof urlValue === "string" ? parseDateRange(urlValue) : null;

        const rawValue = getPersistedRawTimeFilterValue();
        const persistedValue = getPersistedTimeFilterValue();
        const rawRange = rawValue ? parseDateRange(rawValue) : null;
        const persistedRange = persistedValue ? parseDateRange(persistedValue) : null;
        const timeFilterRange = typeof timeFilter?.value === "string" ? parseDateRange(timeFilter.value) : null;

        if (rawValue && !rawRange) {
            console.warn("Invalid raw persisted time filter value, clearing");
            removePersistedRawTimeFilterValue();
        }

        if (persistedValue && !persistedRange) {
            console.warn("Invalid persisted time filter value, clearing");
            removePersistedTimeFilterValue();
        }

        const sourceRange = urlRange || rawRange || persistedRange || timeFilterRange || null;

        // A URL value equal to the applied persisted value was written by saveTimeFilter while
        // adapting the raw range to another table's granularity. Prefer the raw range in that
        // case so a date-only table cannot destroy a more precise minute/second range.
        const urlMatchesAppliedRange = Boolean(urlRange && persistedRange && rangesMatch(urlRange, persistedRange));
        const effectiveSourceRange = urlMatchesAppliedRange ? rawRange || urlRange : sourceRange;

        // Nothing in the URL, nothing persisted, and no existing filter yet — leave it unset.
        // `dateRange` below still shows `defaultRange` visually; we just don't write a filter
        // until the user acts or a URL/persisted value exists, matching this table's original
        // "nothing applied yet" state.
        if (!effectiveSourceRange) return;

        const [start, end] = clampToMode(effectiveSourceRange);
        const newRule = getDefaultCreatedAtFilter([start, end], dataIndex);

        // An explicit URL value is just as authoritative as a manual pick — keep raw storage in
        // sync so navigating to another table preserves it too.
        if (urlRange && !urlMatchesAppliedRange) {
            const rawRule = getDefaultCreatedAtFilter(urlRange, dataIndex);
            if (typeof rawRule.value === "string") {
                setPersistedRawTimeFilterValue(rawRule.value);
            }
        }

        // This effect re-syncs existing filter state (from the URL or persisted storage) on
        // mount/table-switch — it's not a user-initiated filter change, so it must never reset
        // pagination back to page 1. Without `resetPage: false`, this call clobbered a manually
        // edited `page.number` URL param back to 1 on every mount that has a date filter applied
        // (i.e. almost always), since saveTimeFilter defaults to resetting the page.
        tableUtils.saveTimeFilter(newRule, { resetPage: false });
    }, [tableId, dataIndex, dateFilterMode]);

    // derive what the picker shows
    const dateRange: [Dayjs, Dayjs] = useMemo(() => {
        // timeFilter state is the source of truth
        if (timeFilter?.value && typeof timeFilter.value === "string") {
            const parsed = parseDateRange(timeFilter.value);
            if (parsed) return parsed;
        }
        // Fallback to persisted value only for initial load (before timeFilter is set)
        const persistedValue = getPersistedTimeFilterValue();
        if (persistedValue) {
            const parsed = parseDateRange(persistedValue);
            if (parsed) return parsed;
        }
        return defaultRange;
    }, [timeFilter, dataIndex]);

    // only runs when the user *chooses* new dates
    const handleChange = (dates: [Dayjs | null, Dayjs | null]) => {
        if (!dates?.[0] || !dates[1]) return;

        const [start, end] = clampToMode([dates[0], dates[1]]);
        const newRule = getDefaultCreatedAtFilter([start, end], dataIndex);

        // An explicit pick is the new source of truth for the raw (highest-precision) value —
        // it's only ever this granularity because that's what this table's picker exposes.
        if (typeof newRule.value === "string") {
            setPersistedRawTimeFilterValue(newRule.value);
        }

        // saveTimeFilter will update the state and persist the applied value to sessionStorage
        tableUtils.saveTimeFilter(newRule);
    };

    return (
        <div style={{ display: "flex", gap: 12 }}>
            <DateRangePicker
                value={dateRange}
                onChange={handleChange}
                allowClear={false}
                dateFilterMode={dateFilterMode}
            />
        </div>
    );
};
